<template>
  <el-tabs v-model="installSet">
    <el-tab-pane label="Option 1: Recommended Setup" name="normal">
      <div class="mt-4 mb-8">
        <div class="px-6 py-4 bg-brand-sand font-medium rounded-lg shadow-sm my-6 text-brand-black flex space-x-4">
          <div class="flex-grow-0 flex-shrink-0">
            <img src="@/assets/img/web-install.png" class="w-12 h-auto inline-block mt-2" alt="Web Install" />
          </div>
          <div class="space-y-2">
            <p class="font-semibold">Recommended Setup</p>
            <p>
              This streamlined tutorial offers step-by-step instructions for implementing your Churnkey Cancel Flow through 
              our recommended setup. Simply paste our script element within your existing HTML structure (Step One), generate 
              a secure HMAC hash (Step Two); then, launch the Churnkey Cancel Flow from any button (Step Three). For more in-depth details, see
              <a href="https://docs.churnkey.co/installing-churnkey" target="_blank" class="text-brand-blue font-semibold">Cancel Flow Quick Start Guide →</a>
            </p>
          </div>
        </div>
        <p class="text-base font-extrabold text-green-500">Step One</p>
        <p class="text-xl font-extrabold text-brand-black my-2">Place Script Element</p>
        <div class="text-base font-semibold text-brand-brown">
          This code snippet dynamically loads Churnkey functionality. Copy the snippet and paste it within the body tag element
          of your existing HTML structure. Please note that, in the code snippet below, your <code-label text="appId"></code-label> is already inserted in the source link.
        </div>
        <div class="mt-4">
          <pre v-highlightjs="clientSideScript" :key="clientSideScript"><code class="javascript"></code></pre>
        </div>
        <div class="flex justify-end flex-grow-0 mt-6">
          <a href="https://docs.churnkey.co/installing-churnkey?_gl=1*1qfuxw2*_gcl_au*MjM5OTc5MDY5LjE3MTE3MzQxMjA.#block-ed568d0c51bf4792891feab0a2f9689b" target="_blank" class="el-button mx-2 el-button is-plain"
            ><span class="font-bold">See the Docs</span></a
          >
          <el-button type="primary" class="mx-2" @click="copyEmbedSnippet(clientSideScript)"><span class="font-bold">Copy Code</span></el-button>
        </div>
      </div>
      <div class="my-12">
        <p class="text-base font-extrabold text-green-500">Step Two</p>
        <p class="text-xl font-extrabold text-brand-black my-2">Generate Secure HMAC Hash</p>
        <p class="text-base font-semibold text-brand-brown">
          This code snippet demonstrates how to generate a secure HMAC hash using the SHA-256 hashing algorithm in Node.js.
          Generate a secure HMAC hash of the <code-label text="STRIPE_CUSTOMER_ID"></code-label> using the SHA-256 hashing function and your Churnkey API key. 
          This hash is intended to provide a secure means of identifying and authenticating the customer on the front-end during the Churnkey flow.
        </p>
        <p class="text-base font-semibold text-brand-brown mt-2">
          Explore code snippets for implementing server-side HMAC verification in various programming languages
          <a class="text-green-500" href="https://docs.churnkey.co/installing-churnkey?_gl=1*1qfuxw2*_gcl_au*MjM5OTc5MDY5LjE3MTE3MzQxMjA.#block-58daef6b9d30481db1aaee4cad0014c5">
            in our docs.
          </a>
        </p>
        <div class="mt-4">
          <pre v-highlightjs="secretScript" :key="secretScript"><code class="javascript"></code></pre>
        </div>
        <div class="flex justify-end flex-grow-0 mt-6">
          <a href="https://docs.churnkey.co/installing-churnkey?_gl=1*1qfuxw2*_gcl_au*MjM5OTc5MDY5LjE3MTE3MzQxMjA.#block-58daef6b9d30481db1aaee4cad0014c5" target="_blank" class="el-button mx-2 el-button is-plain"
            ><span class="font-bold">See the Docs</span></a
          >
          <el-button v-if="showKey" type="primary" class="mx-2" @click="revealSecret(serverSideScript)">Hide API Key</el-button>
          <el-button v-else type="primary" class="mx-2" @click="revealSecret(serverSideScript)"
            ><span class="font-bold">Reveal API Key</span></el-button
          >
          <el-button v-if="keyShown" type="primary" class="mx-2" @click="copyEmbedSnippet(serverSideScript)"
            ><span class="font-bold">Copy Code</span></el-button
          >
        </div>
      </div>
      <div class="my-12">
        <p class="text-base font-extrabold text-green-500">Step Three</p>
        <p class="text-xl font-extrabold text-brand-black my-2">Launch Churnkey</p>
        <p class="text-base font-semibold text-brand-brown">
          To seamlessly integrate the Churnkey Cancel Flow with any button on your website, use the following code snippet. Replace <code-label text="BUTTON_ID"></code-label> with the ID of your button 
          element. Provide your <code-label text="customerId"></code-label> (obtained from your payment provider) and the authentication hash, <code-label text="authHash"></code-label>, generated on your server side (saved as <code-label text="user_hash"></code-label> 
          from Step Two). This snippet adds an event listener to the specified button which triggers, upon click, the opening of the Cancel Flow Modal.  
        </p>
        <div class="mt-4">
          <pre v-highlightjs="clientSideSampleCode" :key="clientSideSampleCode"><code class="javascript"></code></pre>
        </div>
        <div class="flex justify-end flex-grow-0 mt-6">
          <a
            href="https://docs.churnkey.co/installing-churnkey?_gl=1*1qfuxw2*_gcl_au*MjM5OTc5MDY5LjE3MTE3MzQxMjA.#block-80096b08090f4056995ad133ecf29399"
            target="_blank"
            class="el-button mx-2 el-button is-plain font-bold text-brand-blue hover:text-white"
            >See the Docs</a
          >
          <el-button type="primary" class="mx-2" @click="copyEmbedSnippet(clientSideSampleCode)"
            ><span class="font-bold">Copy Code</span></el-button
          >
        </div>
        <p class="text-base font-semibold text-brand-brown">
          And that's it! For more details, please visit
          <a class="text-green-500" href="https://docs.churnkey.co/installing-churnkey">
             our docs.
          </a>
        </p>
      </div>
    </el-tab-pane>
    <el-tab-pane :disabled="!orgSupportsManagedFlow" label="Option 2: Email Based Verification" name="managed">
      <template #label>
        <el-tooltip
          :content="`Unavailable for ${capitalizeFirstLetter(provider)}`"
          placement="bottom"
          class="custom-tabs-label"
          v-if="!orgSupportsManagedFlow"
        >
          <span>Option 2: Email Based Verification</span>
        </el-tooltip>
        <span v-else>Option 2: Email Based Verification</span>
      </template>
      <div class="mt-4 mb-8">
        <div class="px-6 py-4 bg-brand-sand font-medium rounded-lg shadow-sm my-6 text-brand-black flex space-x-4">
          <div class="flex-grow-0 flex-shrink-0">
            <img src="@/assets/img/email-flow.png" class="w-12 h-auto inline-block mt-2" alt="Email flow" />
          </div>
          <div class="space-y-2">
            <p class="font-semibold">Managed Flow with Customer Email Verification</p>
            <p>
              Our managed email based setup takes care of customer verification for you. When triggered (Step Two), your customer will be asked to
              enter their email address. We'll send a verification code to your customer to verify their account, and then we'll handle the rest.
              This is the easiest way to get started with Churnkey.
              <a href="https://docs.churnkey.co/managed-email-based-flow" target="_blank" class="text-brand-blue font-semibold">More Details →</a>
            </p>
          </div>
        </div>
        <p class="text-base font-extrabold text-green-500">Step One</p>
        <p class="text-xl font-extrabold text-brand-black my-2">Place Embed Code</p>
        <div class="text-base font-semibold text-brand-brown">
          Copy the code below. Paste it before the closing <code-label :text="closeBody"></code-label> tag in your app code,
          or in a <strong>Custom HTML/JavaScript</strong> block in your Content Management System.
        </div>
        <div class="mt-4">
          <pre v-highlightjs="unifiedManagedFlowCode" :key="unifiedManagedFlowCode"><code class="javascript"></code></pre>
        </div>
        <div class="flex justify-end flex-grow-0 mt-6">
          <a href="https://docs.churnkey.co/installing-churnkey" target="_blank" class="el-button mx-2 el-button is-plain"
            ><span class="font-bold">Get Help</span></a
          >
          <el-button type="primary" class="mx-2" @click="copyEmbedSnippet(unifiedManagedFlowCode)"><span class="font-bold">Copy Code</span></el-button>
        </div>
      </div>
      <div class="my-12">
        <p class="text-base font-extrabold text-green-500">Step Two</p>
        <p class="text-xl font-extrabold text-brand-black my-2">Add a Cancel button to your page</p>
        <p class="text-base font-semibold text-brand-brown">
          The way to achieve this depends on whether you are using a Content Management System (CMS) or you write your own HTML code. Either way, you will
          need to add a button with an HTML ID that matches the ID referenced in the code above:
          <pre class="text-green-500 font-mono inline-block mx-1"><code>{{ cancelButtonId }}</code></pre>
        </p>
        <p class="text-base font-semibold text-brand-brown">
          If you can insert custom HTML code, you can use the following code snippet:
        </p>
        <div class="mt-4">
          <pre v-highlightjs="addButtonCode" :key="addButtonCode"><code class="javascript"></code></pre>
        </div>
        <div class="flex justify-end flex-grow-0 mt-6">
          <a href="https://docs.churnkey.co/installing-churnkey" target="_blank" class="el-button mx-2 el-button is-plain"
            ><span class="font-bold">Get Help</span></a
          >
          <el-button type="primary" class="mx-2" @click="copyEmbedSnippet(addButtonCode)"><span class="font-bold">Copy Code</span></el-button>
        </div>
      </div>
    </el-tab-pane>
  </el-tabs>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  singleScriptInstall,
  serverScriptInstall,
  invokeChurnkeyFlow,
  unifiedManagedFlowScript,
  addButtonCode,
  cancelButtonId,
} from '@/components/Cancellation/EmbedCode/snippets';
import OrgService from '@/api/org.service';
import { capitalizeFirstLetter } from '@/store/shared';

export default {
  name: 'EmbedInstructions',
  data() {
    return {
      closeBody: '</body>',
      ckToken: null,
      showKey: false,
      keyShown: false,
      installSet: 'normal', // normal or managed
      capitalizeFirstLetter,
      cancelButtonId,
    };
  },
  components: {
    'code-label': {
      props: ['text'],
      render(h) {
        return h('pre', {
          class: 'text-green-500 font-mono inline-block mx-1'
        }, [h('code', this.text)]);
      }
    }
  },
  created() {
    this.getToken();
  },
  computed: {
    ...mapGetters(['appId', 'provider', 'providerWithVersion', 'isPaddleClassic', 'orgSupportsManagedFlow']),
    secretScript() {
      return this.showKey ? this.serverSideScript : this.serverScriptWithoutSecrets;
    },
    clientSideScript() {
      let script = singleScriptInstall.replace('APP_ID', this.appId || 'APP_ID');
      if (this.providerWithVersion !== 'stripe') {
        script = script.replace('STRIPE_CUSTOMER_ID', `${this.providerWithVersion.toUpperCase()}_CUSTOMER_ID`);
        script = script.replace('stripe', `${this.providerWithVersion.toLowerCase()}`);
      }
      return script;
    },
    serverScriptWithoutSecrets() {
      let script = serverScriptInstall;
      if (this.isPaddleClassic) { // checks if provider is Paddle AND if version is Classic
        script = script.replace('STRIPE_CUSTOMER_ID', 'PADDLE_SUBSCRIPTION_ID');
      } else if (this.providerWithVersion !== 'stripe') {
        script = script.replace('STRIPE_CUSTOMER_ID', `${this.providerWithVersion.toUpperCase()}_CUSTOMER_ID`);
      }
      return script;
    },
    serverSideScript() {
      let script = serverScriptInstall.replace('CK_SECRET', this.ckToken || 'CK_SECRET');
      if (this.isPaddleClassic) { // checks if provider is Paddle AND if version is Classic
        script = script.replace('STRIPE_CUSTOMER_ID', 'PADDLE_SUBSCRIPTION_ID');
      } else if (this.providerWithVersion !== 'stripe') {
        script = script.replace('STRIPE_CUSTOMER_ID', `${this.providerWithVersion.toUpperCase()}_CUSTOMER_ID`);
      }
      return script;
    },
    clientSideSampleCode() {
      let script = invokeChurnkeyFlow.replace('APP_ID', this.appId || 'APP_ID');
      if (this.isPaddleClassic) { // checks if provider is Paddle AND if version is Classic
        script = script.replace('customerId', `subscriptionId`);
        script = script.replace('STRIPE_CUSTOMER_ID', `PADDLE_SUBSCRIPTION_ID`);
        script = script.replace('stripe', 'paddle');
      } else if (this.providerWithVersion !== 'stripe') {
        script = script.replace('STRIPE_CUSTOMER_ID', `${this.providerWithVersion.toUpperCase()}_CUSTOMER_ID`);
        script = script.replace('stripe', `${this.providerWithVersion.toLowerCase()}`);
      }
      return script;
    },
    addButtonCode() {
      return addButtonCode;
    },
    unifiedManagedFlowCode() {
      let script = unifiedManagedFlowScript.replace(/APP_ID/g, this.appId || 'APP_ID');
      if (this.providerWithVersion !== 'stripe') {
        script = script.replace(/STRIPE_CUSTOMER_ID/g, `${this.providerWithVersion.toUpperCase()}_CUSTOMER_ID`);
        script = script.replace(/stripe/g, `${this.providerWithVersion.toLowerCase()}`);
      }
      return script;
    },
    org() {
      return this.$store.state.org;
    },
  },
  methods: {
    async copyEmbedSnippet(snippet) {
      try {
        await this.$copyText(snippet);
        this.$message({
          message: 'Copied to clipboard',
          type: 'success',
        });
      } catch (e) {
        this.$message({
          message: 'Could not copy to clipboard',
          type: 'error',
        });
      }
    },
    revealSecret() {
      this.keyShown = true;
      this.showKey = !this.showKey;
    },
    async getToken() {
      try {
        const res = await OrgService.getToken(this.$store.state.user.org);
        this.ckToken = res.churnkeySecret;
      } catch (e) {
        //
      }
    },
  },
};
</script>
